import React, { useEffect, useState } from 'react';

import { cssMap } from '@compiled/react';
import { token } from '@atlaskit/tokens';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import Form, { FormHeader } from '@atlaskit/form';

import { Text, Stack } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { useIntl } from '@atlassian/jira-intl';

import { fg } from '@atlassian/jira-feature-gating';
import { lookupTypeByTypeId } from '../../../../common/application-types.tsx';
import ProductCard from '../../../common/product-card/index.tsx';
import {
	autoConfigureApplinkConsumer,
	fetchApplinkStatus,
	putApplinkOAuthOne2LOProvider,
	putApplinkOAuthOne3LOProvider,
} from '../../../../rest/index.tsx';
import type { ApplicationLinkV3Status } from '../../../../common/entities.tsx';
import { APPLINKS_LIST_PAGE_URL } from '../../../../common/constants.tsx';
import type { StageConfirmationProps } from '../../../../common/types.tsx';
import { StatusLozengeV3 } from '../../../common/status/lozenge/StatusLozenge.tsx';
import { SubpageTemplate } from '../template/SubpageTemplate.tsx';

const styles = cssMap({
	linkDisplay: {
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
	},
	fullContainer: {
		height: '100%',
	},
});

type ReviewSubpageProps = {
	state: StageConfirmationProps;
};

const ConfirmationSubpage = (props: ReviewSubpageProps): React.JSX.Element => {
	useIntl();

	const [status, setStatus] = useState<ApplicationLinkV3Status | null>(null);

	const finaliseOAuth1Link = async (confirmationApplinkId: string) => {
		await putApplinkOAuthOne3LOProvider(confirmationApplinkId);
		await autoConfigureApplinkConsumer(confirmationApplinkId);
		await putApplinkOAuthOne2LOProvider(confirmationApplinkId);

		setStatus(await fetchApplinkStatus(confirmationApplinkId));
	};

	// eslint-disable-next-line react-you-might-not-need-an-effect/no-reset-all-state-on-prop-change
	useEffect(() => {
		// TODO: Enforce this using discriminated union types
		if (props.state.variant === 'OAUTH2.0') {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
			setStatus(null);
			return;
		}
		finaliseOAuth1Link(props.state.confirmationApplinkId);
	}, [props.state.confirmationApplinkId, props.state.variant]);

	return fg('platform-design_system_team-form--olympus') ? (
		<Form
			onSubmit={() => {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.location.href = APPLINKS_LIST_PAGE_URL;
			}}
			xcss={styles.fullContainer}
		>
			<SubpageTemplate
				variant={props.state.variant}
				kind={props.state.kind}
				buttons={
					<ButtonGroup label="Form submit options">
						{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
						<Button type="submit" appearance="primary" isLoading={status === null}>
							Done
						</Button>
					</ButtonGroup>
				}
				content={
					<Stack>
						<FormHeader title="Confirmation">
							{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
							<Text>The application link has been created.</Text>
						</FormHeader>

						<Stack xcss={styles.linkDisplay} space="space.200">
							{status && (
								<ProductCard
									// TODO: Determine if the type is DC or Cloud
									applicationType={lookupTypeByTypeId(status.link.type, false)}
									displayUrl={status.link.displayUrl}
									heading="Application Link"
									statusLozenge={<StatusLozengeV3 status={status} />}
								/>
							)}
							{status === null && <Spinner size="medium" />}
						</Stack>
					</Stack>
				}
			/>
		</Form>
	) : (
		<Form
			onSubmit={() => {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.location.href = APPLINKS_LIST_PAGE_URL;
			}}
			xcss={styles.fullContainer}
		>
			{({ formProps }) => (
				<form {...formProps}>
					<SubpageTemplate
						variant={props.state.variant}
						kind={props.state.kind}
						buttons={
							<ButtonGroup label="Form submit options">
								{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
								<Button type="submit" appearance="primary" isLoading={status === null}>
									Done
								</Button>
							</ButtonGroup>
						}
						content={
							<Stack>
								<FormHeader title="Confirmation">
									{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
									<Text>The application link has been created.</Text>
								</FormHeader>

								<Stack xcss={styles.linkDisplay} space="space.200">
									{status && (
										<ProductCard
											// TODO: Determine if the type is DC or Cloud
											applicationType={lookupTypeByTypeId(status.link.type, false)}
											displayUrl={status.link.displayUrl}
											heading="Application Link"
											statusLozenge={<StatusLozengeV3 status={status} />}
										/>
									)}
									{status === null && <Spinner size="medium" />}
								</Stack>
							</Stack>
						}
					/>
				</form>
			)}
		</Form>
	);
};

export default ConfirmationSubpage;
